<script setup>
import { PhxButton, PhxStrapFinderPicture } from '@phx-styleguides/iwc';
import { PhxsgButton } from '@sg/src/View/Components/Atoms/Button';
import { computed, ref, watch } from 'vue';
import { props as defaultProps, useSection } from '@maison/composables/strapFinder/useSection';
import { useI18n } from 'vue-i18n';
import ConfiguratorSectionLayout from '@maison/components/strapFinder/sections/ConfiguratorSectionLayout.vue';
import StrapFinderProductCard from '@maison/components/strapFinder/StrapFinderProductCard.vue';
import { useAddToBag } from '@maison/composables/strapFinder/useAddToBag';
import FormGroup from '@maison/components/form/FormGroup.vue';
import RadioListField from '@maison/components/form/fields/RadioListField.vue';

const props = defineProps({
  ...defaultProps,
  reference: {
    type: String,
    required: true,
  },
  defaultStrapSize: {
    type: String,
    required: true,
  },
  defaultStrap: {
    type: Object,
    required: true,
  },
  defaultBuckle: {
    type: Object,
    required: true,
  },
});

const { id } = useSection('replace');
const { isLoading, addReferencesToBag } = useAddToBag();

const { t } = useI18n();

const strapSize = Object.values(props.defaultStrap.sizes).find(
  (size) => size.reference === props.defaultStrap.modelReference,
);

const currentStrapSize = ref(props.defaultStrapSize ?? 'standardSize');
const isSelectingSize = ref(false);

const products = ref([
  {
    reference: strapSize.reference,
    name: props.defaultStrap.name,
    media: {
      id: props.defaultStrap.modelReference,
      captionText: props.defaultStrap.name,
      ...props.defaultStrap.image,
    },
    image: props.defaultStrap.image,
    price: strapSize.priceWithCurrency,
    size: t(`StrapFinderConfigurator.sizes.${currentStrapSize.value}`),
    editable: true,
    edit: editStrapSize,
  },
  {
    reference: props.defaultBuckle.modelReference,
    name: props.defaultBuckle.name,
    image: props.defaultBuckle.image,
    price: props.defaultBuckle.priceWithCurrency,
    removable: true,
  },
]);

const configuratorCustomizeLink = computed(() => {
  return `${window.route('strap_finder_configurator_page')}?reference=${props.reference}`;
});

const strapSizeField = computed(() => {
  if (!currentStrapSize.value) return null;

  return {
    id: 'strap-size-edit',
    name: 'strap-size-edit',
    label: '',
    options: Object.entries(props.defaultStrap.sizes).map(([size, { priceWithCurrency, inStock, sellable }]) => ({
      id: size,
      value: size,
      label: t(`StrapFinderConfigurator.sizes.${size}`),
      additionalContent: priceWithCurrency,
      disabled: !inStock || !sellable,
    })),
  };
});

function removeProduct(reference) {
  products.value = products.value.filter((product) => product.reference !== reference);
}

function editStrapSize() {
  isSelectingSize.value = true;
}

watch(currentStrapSize, (newSize) => {
  isSelectingSize.value = false;

  products.value[0].reference = props.defaultStrap.sizes[newSize].reference;
  products.value[0].price = props.defaultStrap.sizes[newSize].price;
  products.value[0].size = t(`StrapFinderConfigurator.sizes.${newSize}`);
});
</script>

<template>
  <configurator-section-layout :id="id" :title="title" :description="description" :memo="memo">
    <template #media>
      <phx-strap-finder-picture :id="`${id}-media`" :caption-text="defaultStrap.name" v-bind="defaultStrap.image" />
    </template>

    <template #default>
      <div v-if="isSelectingSize">
        <PhxsgButton
          class="btn btn--icon btn--fade mb-4"
          icon-id="arrow-left"
          :label="$t('StrapFinderConfigurator.replace.back')"
          @click="() => (isSelectingSize = false)"
        />
        <form-group :attrs="strapSizeField" :is-fieldset="true">
          <radio-list-field v-model="currentStrapSize" type="rich" />
        </form-group>
      </div>
      <div v-else class="stack stack--spacing-4">
        <strap-finder-product-card
          v-for="product in products"
          :key="product.reference"
          v-bind="product"
          @edit-product="product?.edit"
          @remove-product="removeProduct"
        />
      </div>
    </template>

    <template #action>
      <phx-button
        :loading="isLoading"
        :disabled="isSelectingSize"
        class="btn--plain btn--primary"
        :label="$t('StrapFinderConfigurator.replace.addToBag')"
        @click="addReferencesToBag(products.map((p) => p.reference))"
      />
      <phx-button
        tag="a"
        :href="configuratorCustomizeLink"
        class="btn--plain btn--secondary"
        :label="$t('StrapFinderConfigurator.replace.customize')"
      />
    </template>
  </configurator-section-layout>
</template>
